import { useState } from "react";
import { toast } from "react-toastify";
import {
  EnvelopeIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { ICallFollowUpEmailDraft, ICallListItem } from "shared";

import { Button } from "../../../common/Atoms/Button";
import { InputTextAddon } from "../../../common/Atoms/InputTextAddon";
import { TextArea } from "../../../common/Atoms/TextArea";
import { classNames } from "../../../common/lib/classNames";
import { useDraftCallFollowUpEmailMutation, useSendCallFollowUpEmailMutation } from "../../services/api/contactsApi/contact";

// Same limits as the contact page composer
const BODY_MIN_LENGTH = 4;
const BODY_MAX_LENGTH = 10000;
const SUBJECT_MAX_LENGTH = 255;

function apiError(err: unknown, fallback: string) {
  return (err as { data?: { message?: string } })?.data?.message || fallback;
}

interface ICallFollowUpEmailProps {
  call: ICallListItem;
  organisationId: string;
  /** After a send, so the call's recorded follow ups pick up the email */
  onSent: () => void;
}

/**
 * Drafts an email recapping the call from its transcript, for the user to edit and send to the customer. The sent
 * email is recorded against the call
 */
export function CallFollowUpEmail({ call, organisationId, onSent }: ICallFollowUpEmailProps) {
  const [draftEmail, { isLoading: drafting }] = useDraftCallFollowUpEmailMutation();
  const [sendEmail, { isLoading: sending }] = useSendCallFollowUpEmailMutation();
  const [draft, setDraft] = useState<ICallFollowUpEmailDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ids = { organisationId, contactId: call.contactId, callId: call.id };
  const subject = draft?.subject.trim() || ``;
  const body = draft?.body.trim() || ``;
  const canSend = !!draft?.toEmails.length && !!subject && subject.length <= SUBJECT_MAX_LENGTH
    && body.length >= BODY_MIN_LENGTH && body.length <= BODY_MAX_LENGTH;

  async function onDraft() {
    setError(null);

    try {
      setDraft(await draftEmail(ids).unwrap());
    }
    catch (err) {
      setError(apiError(err, `Could not draft the email. Please try again.`));
    }
  }

  async function onSend() {
    if (!draft || !canSend) return;

    setError(null);

    try {
      await sendEmail({ ...ids, toEmails: draft.toEmails, subject, body }).unwrap();
      setDraft(null);
      toast.success(`Follow up email sent to ${call.contactName}`);
    }
    catch (err) {
      setError(apiError(err, `Could not send the email. Please try again.`));
    }
    finally {
      // A failed send is still recorded against the call
      onSent();
    }
  }

  function toggleRecipient(email: string) {
    if (!draft) return;

    const toEmails = draft.toEmails.includes(email) ? draft.toEmails.filter(item => item !== email) : [...draft.toEmails, email];

    setDraft({ ...draft, toEmails });
  }

  if (!draft) {
    return (
      <div>
        <p className={ `text-sm text-gray-500` }>{ `Draft an email to the customer recapping the call. You can edit it before it goes.` }</p>
        <Button
          className={ `mt-2` }
          size={ `sm` }
          color={ `white` }
          onClick={ onDraft }
          loading={ drafting }
          disabled={ drafting }
        >
          <EnvelopeIcon className={ `mr-1 h-3.5 w-3.5` } />
          { `Draft follow up email` }
        </Button>
        { error ? <p className={ `mt-2 text-xs text-red-600` }>{ error }</p> : null }
      </div>
    );
  }

  return (
    <div className={ `space-y-3 rounded-lg border border-gray-200 p-3` }>
      <div>
        <span className={ `mb-1 block text-xs font-medium text-gray-600` }>{ `To` }</span>
        <div className={ `flex flex-wrap gap-1.5` }>
          { draft.availableEmails.map(email => {
            const selected = draft.toEmails.includes(email);

            return (
              <button
                key={ email }
                type={ `button` }
                aria-pressed={ selected }
                onClick={ () => toggleRecipient(email) }
                disabled={ sending }
                className={ classNames(
                  `inline-flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition disabled:cursor-not-allowed`,
                  selected ? `border-paidniceGreen-300 bg-paidniceGreen-50 text-paidniceGreen-800` : `border-dashed border-gray-300 bg-white text-gray-500 hover:bg-gray-50`,
                ) }
              >
                { selected ? <XMarkIcon className={ `h-3.5 w-3.5` } /> : <PlusIcon className={ `h-3.5 w-3.5` } /> }
                { email }
              </button>
            );
          }) }
        </div>
      </div>

      <InputTextAddon
        label={ `Subject` }
        name={ `followUpSubject` }
        value={ draft.subject }
        onChange={ ({ value }) => setDraft({ ...draft, subject: String(value) }) }
        disabled={ sending }
      />

      <TextArea
        label={ `Message` }
        name={ `followUpBody` }
        value={ draft.body }
        rows={ 10 }
        onChange={ ({ value }) => setDraft({ ...draft, body: String(value) }) }
        disabled={ sending }
        description={ `Your sender profile's signature, if it has one, goes below this.` }
      />

      { error ? <p className={ `text-xs text-red-600` }>{ error }</p> : null }

      <div className={ `flex justify-end gap-2` }>
        <Button
          size={ `sm` }
          color={ `white` }
          onClick={ () => setDraft(null) }
          disabled={ sending }
        >
          { `Discard` }
        </Button>
        <Button
          size={ `sm` }
          onClick={ onSend }
          loading={ sending }
          disabled={ !canSend || sending }
        >
          { `Send email` }
        </Button>
      </div>
    </div>
  );
}
